"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { useQueryClient } from "@tanstack/react-query";
import { Flag, Lock, LockOpen, MoreHorizontal, Pin, PinOff, Trash2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/app/_module/components/ui/dropdown-menu";
import { Button } from "@/app/_module/components/ui/button";
import { useToast } from "@/app/_module/components/ui/use-toast";
import { forumService, ForumPostSummary, forumPath } from "./ForumService";
import { useForumAccess } from "../hooks/useForumAccess";
import { ReportDialog } from "./ReportDialog";
import { ConfirmDeleteDialog } from "./ConfirmDeleteDialog";

// Per-post overflow menu: report for everyone; pin/lock/remove for platform
// admins; delete for the author.
export function PostActionsMenu({
  post,
  isOwn,
  stayOnDelete = false,
}: {
  post: ForumPostSummary & { isOwn?: boolean };
  isOwn?: boolean;
  // Post page passes true: after deleting, stay and show the [deleted]
  // tombstone in place (feed cards keep the default: card just disappears).
  stayOnDelete?: boolean;
}) {
  const { isModerator, requireWrite } = useForumAccess();
  const [reportOpen, setReportOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [modRemoveOpen, setModRemoveOpen] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const router = useRouter();
  const own = isOwn ?? (post as any).isOwn;

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["forum", "posts"] });
    queryClient.invalidateQueries({ queryKey: ["forum", "post"] });
  };

  const modAction = async (action: "pin" | "unpin" | "lock" | "unlock") => {
    try {
      await forumService.mod.postAction(post.id, action);
      invalidate();
      toast({ title: `Post ${action}${action.endsWith("n") ? "ned" : "ed"}`, duration: 2500 });
    } catch (e: any) {
      toast({ title: "Action failed", description: e.message, variant: "destructive" });
    }
  };

  const removeAsMod = async (reason?: string) => {
    try {
      await forumService.mod.removePost(post.id, reason || "Removed by moderator");
      invalidate();
      toast({ title: "Post removed", duration: 2500 });
    } catch (e: any) {
      toast({ title: "Action failed", description: e.message, variant: "destructive" });
    }
  };

  const deleteOwn = async () => {
    try {
      await forumService.deletePost(post.id);
      invalidate();
      toast({ title: "Post deleted — it now shows as [deleted]", duration: 3500 });
      if (!stayOnDelete) router.push(forumPath());
    } catch (e: any) {
      toast({ title: "Delete failed", description: e.message, variant: "destructive" });
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-muted-foreground opacity-0 group-hover:opacity-100 focus:opacity-100 data-[state=open]:opacity-100">
            <MoreHorizontal className="h-3.5 w-3.5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-44">
          <DropdownMenuItem onClick={() => requireWrite(() => setReportOpen(true))}>
            <Flag className="h-3.5 w-3.5 mr-2" /> Report
          </DropdownMenuItem>
          {own && (
            <DropdownMenuItem onClick={() => setDeleteOpen(true)} className="text-destructive focus:text-destructive">
              <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete post
            </DropdownMenuItem>
          )}
          {isModerator && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => modAction(post.isPinned ? "unpin" : "pin")}>
                {post.isPinned ? <PinOff className="h-3.5 w-3.5 mr-2" /> : <Pin className="h-3.5 w-3.5 mr-2" />}
                {post.isPinned ? "Unpin" : "Pin"}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => modAction(post.isLocked ? "unlock" : "lock")}>
                {post.isLocked ? <LockOpen className="h-3.5 w-3.5 mr-2" /> : <Lock className="h-3.5 w-3.5 mr-2" />}
                {post.isLocked ? "Unlock" : "Lock"}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setModRemoveOpen(true)} className="text-destructive focus:text-destructive">
                <Trash2 className="h-3.5 w-3.5 mr-2" /> Remove (mod)
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <ReportDialog open={reportOpen} onOpenChange={setReportOpen} targetType="post" targetId={post.id} />
      <ConfirmDeleteDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Delete your post?"
        description="The post leaves the feeds and shows as [deleted] — the comment thread underneath stays visible. This can't be undone."
        confirmLabel="Delete post"
        onConfirm={deleteOwn}
      />
      <ConfirmDeleteDialog
        open={modRemoveOpen}
        onOpenChange={setModRemoveOpen}
        title="Remove this post?"
        description="It will show as [removed] to the community. The reason is kept on record."
        confirmLabel="Remove post"
        requireReason
        onConfirm={removeAsMod}
      />
    </>
  );
}
